import { FC, useCallback, useEffect, useState } from 'react'
import Cropper, { Area } from 'react-easy-crop'

interface ImageCropperProps {
  file: File | null
  aspect?: number // ratio largeur/hauteur, défaut 2/1 (galerie hôtel)
  title?: string
  onCropped: (croppedFile: File, previewUrl: string) => void
  onCancel: () => void
}

// Génère un blob croppé à partir d'une image + zone de crop, via canvas.
// Renvoie un File prêt à être envoyé via FormData.
async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area,
  fileName = 'cropped-image.jpg'
): Promise<File> {
  const image = new Image()
  image.src = imageSrc
  await new Promise((resolve) => {
    image.onload = resolve
  })

  const canvas = document.createElement('canvas')
  canvas.width = pixelCrop.width
  canvas.height = pixelCrop.height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas context unavailable')

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  )

  return new Promise<File>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'))
          return
        }
        resolve(new File([blob], fileName, { type: 'image/jpeg' }))
      },
      'image/jpeg',
      0.92
    )
  })
}

const ImageCropper: FC<ImageCropperProps> = ({
  file,
  aspect = 2,
  title = 'Recadrer l\'image',
  onCropped,
  onCancel,
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)

  useEffect(() => {
    if (!file) {
      setImageSrc(null)
      return
    }
    const reader = new FileReader()
    reader.onload = () => setImageSrc(reader.result as string)
    reader.readAsDataURL(file)
  }, [file])

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels)
  }, [])

  const handleValidate = async () => {
    if (!imageSrc || !croppedAreaPixels) return
    try {
      const croppedFile = await getCroppedImg(imageSrc, croppedAreaPixels, file?.name || 'cropped-image.jpg')
      const previewUrl = URL.createObjectURL(croppedFile)
      onCropped(croppedFile, previewUrl)
    } catch (err) {
      console.error('Crop error:', err)
    }
  }

  if (!imageSrc) return null

  return (
    <div className='modal modal-style d-block' style={{ zIndex: 1060 }}>
      <div className='modal-dialog modal-lg'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>{title}</h5>
            <button type='button' className='btn-close' onClick={onCancel} />
          </div>
          <div className='modal-body'>
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: 400,
                background: '#000',
                overflow: 'hidden',
                borderRadius: 8,
              }}
            >
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className='mt-3'>
              <label className='form-label me-2'>Zoom</label>
              <input
                type='range'
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>
            <div className='form-text text-muted mt-1'>
              Ratio cible : {aspect.toFixed(2)} ({aspect >= 1.9 && aspect <= 2.1 ? 'galerie' : 'image'})
            </div>
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-secondary' onClick={onCancel}>
              Annuler
            </button>
            <button type='button' className='btn btn-primary' onClick={handleValidate}>
              Valider le recadrage
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export { ImageCropper }
